/**
 * Licensed Materials - Property of IBM
 * IBM Cognos Products: BI
 * (C) Copyright IBM Corp. 2019
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

import React, { Component } from 'react';
import PropTypes from 'prop-types';
import StringResources from 'nls/StringResources';
import {
	Button,
	Container,
	FlexItem,
	FlexLayout,
	HSpacer,
	Label,
	TextInput,
	VSpacer
} from '@ba/ba-ui-carbon-toolkit';
import { Link, TextArea } from '@ba/ba-ui-carbon';

import '../../../stylesheets/all.scss';
import FileLoader from '../../utilities/FileLoader';
import closeIcon from '@ba/ba-graphics/dist/icons-js/dismiss_16';

const ICON_ADD = 'add';
const ICON_REMOVE = 'remove';
const ICON_FILE_TYPES = '.svg .jpg .png';

export default class PackagePanel extends Component {
	static defaultProps = {
		iconName: '',
		iconURL: '',
		packageDescription: ''
	};

	static propTypes = {
		packageName: PropTypes.string.isRequired,
		packageDescription: PropTypes.string.isRequired,
		iconURL: PropTypes.string,
		iconName: PropTypes.string,
		zipMaxSize: PropTypes.number,
		imgMaxSize: PropTypes.number,
		onChange: PropTypes.func.isRequired,
		onIconChanged: PropTypes.func.isRequired,
		onIconError: PropTypes.func.isRequired
	};

	constructor( props ) {
		super( props );

		this.state = {
			isDragging: false
		};

		this.inputRef = null;
		this.dndOverlay = null;
	}

	//
	// Utility functions
	//
	_showOverlay = () => {
		this.setState({ isDragging: true });
	}

	_hideOverlay = () => {
		this.setState({ isDragging: false });
	}

	_findSourceName = ( _name ) => {
		return _name.split('/').pop();
	}

	_getUrlCreator = () => {
		return window.URL || window.webkitURL;
	}

	//
	// Event handlers
	//

	// The package name has changed
	_handlePackageNameChange = ( _value ) => {
		this.props.onChange( { 'name': 'name', 'value': _value } );
	}

	// The package description has changed
	_handlePackageDescriptionChange = ( synthEvent ) => {
		this.props.onChange( { 'name': 'description', 'value': synthEvent.target.value } );
	}

	_handleDragEnter = () => {
		this._showOverlay();
	}

	_handleDragOver = ( _event ) => {
		_event.dataTransfer.dropEffect = 'copy';
		_event.preventDefault();
		_event.stopPropagation();
	}

	_handleDragEnd = () => {
		this._hideOverlay();
	}

	_handleDragLeave = ( _event ) => {
		this._hideOverlay();
		_event.preventDefault();
	}

	_handleDrop = ( _event ) => {
		this._hideOverlay();
		let failed = false;
		// Check that the file type is '.svg'
		for( let i = 0; i < _event.dataTransfer.files.length; i++ ) {
			let file = _event.dataTransfer.files[ i ];
			if ( ICON_FILE_TYPES.indexOf( file.name.toLowerCase().substr( -4 ) ) < 0 ) {
				this.props.onIconError( new Error( StringResources.get('schematicsInvalidType', { type: file.name } ) ) );
				failed = true;
				break;
			}
		}

		if ( !failed )
			this._handleIconChange( ICON_ADD, _event.dataTransfer.files );
		_event.preventDefault();
	}

	_handleIconChange = ( _action, _files ) => {
		const { zipMaxSize, imgMaxSize, onIconError, onIconChanged } = this.props;
		if ( _action === 'add' ) {
			let loader = new FileLoader( null, zipMaxSize, imgMaxSize, ICON_FILE_TYPES );
			loader.load( _files, ( _fileList ) => {
				if ( _fileList.length > 0 ) {
					let item = _fileList[ 0 ];
					let blobUrl = null;
					let sourceName = this._findSourceName( item );
					let loadPromise = loader.getFileAsBlob( item );

					loadPromise.then( ( loadedBlobUrl ) => {
						blobUrl = this._getUrlCreator().createObjectURL( loadedBlobUrl );
						onIconChanged( sourceName, blobUrl );
					} ).catch( onIconError );
				}
			},
			onIconError );
		} else if ( _action ==='remove' ) {
			onIconChanged( '', '' );
		}
	}

	_renderIconRow() {
		const { iconURL, iconName } = this.props;
		if ( iconURL ) {
			return <FlexLayout
				className='clsSVGIconRow'
				direction='row'
				justifyContent='flex-start'
				alignItems='center'
			>
				<FlexItem>
					<FlexLayout id='svgIconFL'>
						<FlexItem id='svgIconFI' style={{ backgroundImage: 'url(' + iconURL + ')' }}>
						</FlexItem>
					</FlexLayout>
				</FlexItem>
				<FlexItem grow={true}>
					<FlexLayout direction='row' justifyContent='flex-start'>
						<FlexItem style={{ height: '18px' }}>
							<Label id='idSchematicIconLabel' type='caption' htmlFor='idSchematicIcon' label={iconName} ellipsis/>
						</FlexItem>
					</FlexLayout>
				</FlexItem>
				<FlexItem>
					<Button
						intent='primary'
						icon={closeIcon.id}
						iconSize='small'
						variant='icon'
						onClick={ () => this._handleIconChange( ICON_REMOVE, null ) }
						title={StringResources.get('schematicsDeleteIcon')}
						tabIndex={0}
					/>
				</FlexItem>
			</FlexLayout>;
		} else
			return;
	}

	_renderDropZone() {
		const dropLabel = StringResources.get( 'schematicsIconDropFiles' );

		return <FlexLayout
			fullHeight={true}
			direction='column'
		>
			<FlexItem style={ { margin: '16px' } }>
				<Link href="javascript:void()" onClick={() => this.inputRef.click()}>{dropLabel}</Link>
			</FlexItem>
		</FlexLayout>;
	}

	_renderIconContents() {
		if ( this.props.iconURL )
			return this._renderIconRow();
		// This component keeps a simple state of whether we are dragging on not.
		const overlayDisplayStyle = this.state.isDragging ? 'block' : 'none';
		const fileTypesLabel = StringResources.get( 'schematicsIconFileTypes' );
		const { imgMaxSize } = this.props;
		const fileSizeLabel = StringResources.get( 'schematicsIconFileSize', { size: imgMaxSize } );
		return (
			<FlexLayout direction='column' fullHeight={true}>
				<FlexItem>
					<Label className='clsSVGFileTypeLabel' label={fileTypesLabel}/>
				</FlexItem>
				<FlexItem>
					<Label className='clsSVGFileSizeLabel' label={fileSizeLabel}/>
				</FlexItem>
				<FlexItem>
					<VSpacer size={2} />
				</FlexItem>
				<FlexItem grow={1} className='clsSVGIconPanel'>
					<Container
						onDragEnter={(e)=>this._handleDragEnter( e )}
						className={this.state.isDragging ? 'clsSVGDropZone dragging' : 'clsSVGDropZone'}
						droppable='true'
					>
						<Container
							className='clsSVGDndOverlay'
							onDragOver={(e)=>this._handleDragOver( e )}
							onDragEnd={(e)=>this._handleDragEnd( e )}
							onDragLeave={(e)=>this._handleDragLeave( e )}
							onDrop={(e)=>this._handleDrop( e )}
							style={{ display: overlayDisplayStyle }}
							ref={(ref) => {this.dndOverlay = ref;}}
						/>{this._renderDropZone()}
					</Container>
				</FlexItem>
				<FlexItem>
					<VSpacer size={2} />
				</FlexItem>
				<FlexItem>
					<input
						ref={(ref) => { this.inputRef = ref;} }
						id='idChooseFile_Input'
						accept='.svg, .jpg, .png'
						type='file'
						onChange={() => this._handleIconChange( ICON_ADD, this.inputRef.files )}
						style={{ display: 'none' }}
					>
					</input>
					<Button
						id='idChooseFile_Button'
						intent='primary'
						onClick={() => this.inputRef.click()}
						label={StringResources.get( 'schematicsChooseFile' )}
						tabIndex={0}
					/>
				</FlexItem>
			</FlexLayout>
		);
	}

	//
	// Main render function
	//
	render () {
		const { packageName, packageDescription } = this.props;
		// Get the i18n strings
		const packageNameLabel = StringResources.get('nameLabelSchematic');
		const packageDescriptionLabel = StringResources.get('schematicsDescriptionLabel');
		const iconLabel = StringResources.get('schematicsIconLabel');

		return (
			<FlexLayout
				className="clsSVGTabsStyle"
				direction='row'
				style={{ width: '100%' }}
			>
				<FlexItem>
					<FlexLayout direction='row'>
						<FlexItem>
							<FlexLayout className='clsSVGDetailsPanel'
								direction='column'
							>
								<FlexItem>
									<VSpacer size={2} />
								</FlexItem>
								<FlexItem>
									<Label type='caption' htmlFor='packageNameEdit' label={packageNameLabel} />
								</FlexItem>
								<FlexItem className='clsSVGPackageEdit'>
									<TextInput
										id='packageNameEdit'
										placeholder={packageNameLabel}
										value={packageName}
										onValueChange={this._handlePackageNameChange}
										light
									/>
								</FlexItem>
								<FlexItem>
									<VSpacer size={3} />
								</FlexItem>
								<FlexItem className="clsSVGPackageEdit">
									<TextArea style={ { resize: 'none' } }
										id='packageDescriptionEdit'
										placeholder={packageDescriptionLabel}
										rows={6}
										maxLength={100}
										value={packageDescription}
										labelText={packageDescriptionLabel}
										onChange={this._handlePackageDescriptionChange}
										light
									/>
								</FlexItem>
							</FlexLayout>
						</FlexItem>
						<FlexItem>
							<HSpacer size={4}/>
						</FlexItem>
						<FlexItem grow={1}>
							<FlexLayout direction='column' fullHeight={true}>
								<FlexItem>
									<VSpacer size={2} />
								</FlexItem>
								<FlexItem>
									<Label type='caption' htmlFor='iconDropZone' label={iconLabel} className='clsSVGPackageIcon'/>
								</FlexItem>
								{ this._renderIconContents() }
							</FlexLayout>
						</FlexItem>
					</FlexLayout>
				</FlexItem>
			</FlexLayout>
		);
	}
}



// WEBPACK FOOTER //
// ./src/js/dialog/schematic/PackagePanel.js